import classnames from 'classnames'
import { useState, useRef } from 'react'
import { useClickOutsideListener } from '../hooks/index.js'

const Option = (props = {}) => {
    const {
        label,
        value,
        name,
        type = "checkbox",
        isActive = false,
        id,
        onChange
    } = props;

    return (
        <label htmlFor={id}
            className="cursor-pointer flex hover:bg-black hover:bg-opacity-5 items-center p-2 transition-all duration-300">
            <input
                name={name}
                id={id}
                type={type}
                checked={isActive}
                className="h-4 w-4 text-blue focus:ring-blue border-gray checked:border-blue rounded-sm checked:rounded"
                value={value}
                onChange={(e) => { onChange(e) }}
            />
            <span className="ml-4 block text-base text-gray-black">{label}</span>
        </label>
    )
}

const OptionList = (props) => {
    const {
        options = [],
        name,
        isActive = false,
        onOptionActiveStateChange
    } = props;

    const children = options
        .sort((a, b) => a.label < b.label ? -1 : 1)
        .map(option => (
            <Option
                label={option.label}
                value={option.value}
                name={name}
                key={`${name}_${option.value}`}
                id={`${name}_${option.value}`}
                isActive={option.isActive}
                type={option.type}
                onChange={() => {
                    onOptionActiveStateChange({ ...option, isActive: !option.isActive })
                }}
            />
        ))

    const className = classnames(
        'grid',
        'border',
        'border-blue',
        'bg-white',
        'absolute',
        'z-50',
        {
            hidden: !isActive
        }
    )

    return (
        <div className={className}>
            {children}
        </div>
    )
}

const OptionButton = (props = {}) => {
    const {
        label,
        isActive,
        onClick
    } = props
    const className = classnames(
        'grid-col-2-maxcontent',
        'inline-grid',
        'items-center',
        'p-2',
        'cursor-pointer',
        'focus:outline-none',
        {
            'transition-all': !isActive,
            'duration-300': !isActive,
            'rounded-md': !isActive,
            'text-blue': !isActive,
            'hover:bg-opacity-5': !isActive,
            'hover:bg-black': !isActive,
            'rounded-t-md': isActive,
            'bg-blue': isActive,
            'text-white': isActive
        }
    )

    return (
        <button className={className} onClick={(e) => { onClick(e) }}>
            <label id="listbox-label" className="block font-semibold text-base cursor-pointer">{label.replaceAll('_', ' ')}</label>
            <svg className="fill-current h-4 ml-2 w-4 cursor-pointer" aria-hidden="true" viewBox="0 0 52 52">
                <path xmlns="http://www.w3.org/2000/svg"
                    d="M47.6 17.8L27.1 38.5c-.6.6-1.6.6-2.2 0L4.4 17.8c-.6-.6-.6-1.6 0-2.2l2.2-2.2c.6-.6 1.6-.6 2.2 0l16.1 16.3c.6.6 1.6.6 2.2 0l16.1-16.2c.6-.6 1.6-.6 2.2 0l2.2 2.2c.5.6.5 1.5 0 2.1z">
                </path>
            </svg>
        </button>
    )
}

const FilterControl = (props = {}) => {
    const {
        label,
        id,
        name,
        options = [],
        onChange
    } = props;

    const [isActive, setIsActive] = useState(props.isActive || false)

    const wrapperRef = useRef(null)

    useClickOutsideListener(wrapperRef, () => setIsActive(false))

    return (
        <div className="mr-4 relative" ref={wrapperRef}>
            <OptionButton
                label={label}
                isActive={isActive}
                onClick={() => setIsActive(isActive => !isActive)}
            />
            <OptionList
                name={name}
                options={options}
                isActive={isActive}
                onOptionActiveStateChange={(changedOpt) => {
                    onChange({
                        label,
                        id,
                        name,
                        options: [
                            ...options.filter(opt => opt.value !== changedOpt.value),
                            changedOpt
                        ]
                    })
                }}
            />

        </div>
    )
}

const FilterControlList = (props = {}) => {
    const {
        filters = [],
        onChange
    } = props

    const controls = filters.sort((a, b) => a.label <= b.label ? -1 : 1).map(filter => (
        <FilterControl
            id={filter.id}
            label={filter.label}
            name={filter.name}
            isActive={filter.isActive}
            options={filter.options}
            key={filter.name}
            onChange={(changedFilter) => onChange(changedFilter)}
        />
    ))

    return (
        <div className="flex flex-wrap">{controls}</div>
    )
}

const ActiveFilter = (props = {}) => {
    return (
        <div className="bg-gray-light gap-3 grid-flow-col inline-grid items-center mr-2 my-1 pl-2 rounded-sm">
            <div className="text-xs text-gray-darkest">{props.label}</div>
            <button className="duration-300 hover:bg-black hover:bg-opacity-5 p-2 self-stretch transition-all" onClick={() => {
                props.onChange({ label: props.label })
            }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52"
                    className="w-3.5 h-3.5 fill-current text-gray-dark">
                    <path
                        d="M31 25.4l13-13.1c.6-.6.6-1.5 0-2.1l-2-2.1c-.6-.6-1.5-.6-2.1 0L26.8 21.2c-.4.4-1 .4-1.4 0L12.3 8c-.6-.6-1.5-.6-2.1 0l-2.1 2.1c-.6.6-.6 1.5 0 2.1l13.1 13.1c.4.4.4 1 0 1.4L8 39.9c-.6.6-.6 1.5 0 2.1l2.1 2.1c.6.6 1.5.6 2.1 0L25.3 31c.4-.4 1-.4 1.4 0l13.1 13.1c.6.6 1.5.6 2.1 0L44 42c.6-.6.6-1.5 0-2.1L31 26.8c-.4-.4-.4-1 0-1.4z">
                    </path>
                </svg>
            </button>
        </div>
    )
}

const ActiveFilterList = (props = {}) => {
    const {
        items = [],
        onChange
    } = props;

    if (!items || !Array.isArray(items) || items.length < 1) return null

    const activeFilters = items.map(item => (
        <ActiveFilter
            label={item.label}
            key={`${item.name}_${item.value}`}
            onChange={() => {
                onChange({
                    removedItem: item
                })
            }}
        />
    ))

    return (
        <div className="justify-start">
            {activeFilters}
            <button
                className="text-xs p-2 rounded-sm text-blue hover:bg-gray-dark hover:bg-opacity-5 transition-all duration-300">
                Clear all filters
            </button>
        </div>
    )
}

export const CardCollectionFilterControlsPanel = (props = {}) => {
    const {
        isEnabled = false,
        filters = [],
        onChange
    } = props

    if (!isEnabled) return null

    return (
        <>
            <FilterControlList
                filters={filters}
                onChange={(changedFilter) => {
                    onChange({
                        filters: [
                            ...filters.filter(filter => filter.name !== changedFilter.name),
                            changedFilter
                        ]
                    })
                }}
            />
            <ActiveFilterList
                items={filters.reduce((list, filter) => {
                    return [
                        ...list,
                        ...filter?.options?.filter(opt => opt.isActive) || []
                    ].map(item => ({
                        ...item, name: filter.name
                    }))
                }, [])}
                onChange={({ removedItem }) => {
                    const newFilters = []
                    for (const filter of filters) {
                        if (removedItem.name < filter.name) {
                            newFilters.push(filter)
                            continue
                        }
                        const options = filter.options.map(opt => ({
                            ...opt,
                            isActive: removedItem.value === opt.value ? false : opt.isActive
                        }))

                        newFilters.push({
                            ...filter,
                            options
                        })
                    }
                    onChange({
                        filters: newFilters
                    })
                }}
            />
        </>
    )
};
export default CardCollectionFilterControlsPanel